import { useState, useCallback } from 'react';
// eslint-disable-next-line no-unused-vars
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  RefreshCw,
  Users,
  Settings,
  Download,
  LogOut,
  X,
  ChevronRight,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import KioskAdminLogin from '../KioskAdminLogin';
import KioskAdminDashboard from './KioskAdminDashboard';
import KioskAdminSync from './KioskAdminSync';
import KioskAdminEmployees from './KioskAdminEmployees';
import KioskAdminSettings from './KioskAdminSettings';
import KioskAdminInstall from './KioskAdminInstall';

// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------
const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'sync', label: 'Sync & Export', icon: RefreshCw },
  { id: 'employees', label: 'Employees', icon: Users },
  { id: 'settings', label: 'Settings', icon: Settings },
  { id: 'install', label: 'Install App', icon: Download },
];

// ---------------------------------------------------------------------------
// Content transition
// ---------------------------------------------------------------------------
const CONTENT_VARIANTS = {
  initial: { opacity: 0, x: 12 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -12 },
};

// ---------------------------------------------------------------------------
// KioskAdminPanel
// ---------------------------------------------------------------------------
export default function KioskAdminPanel({ syncEngine, deviceInfo, onClose, clearDevice }) {
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLoginSuccess = useCallback(() => {
    setAuthenticated(true);
  }, []);

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  }, []);

  // -----------------------------------------------------------------------
  // Auth gate: show login screen first
  // -----------------------------------------------------------------------
  if (!authenticated) {
    return (
      <KioskAdminLogin
        onSuccess={handleLoginSuccess}
        onCancel={onClose}
      />
    );
  }

  // -----------------------------------------------------------------------
  // Render the active tab content
  // -----------------------------------------------------------------------
  function renderContent() {
    switch (activeTab) {
      case 'dashboard':
        return <KioskAdminDashboard syncEngine={syncEngine} />;
      case 'sync':
        return <KioskAdminSync syncEngine={syncEngine} />;
      case 'employees':
        return <KioskAdminEmployees syncEngine={syncEngine} />;
      case 'settings':
        return (
          <KioskAdminSettings
            deviceInfo={deviceInfo}
            syncEngine={syncEngine}
            clearDevice={clearDevice}
          />
        );
      case 'install':
        return <KioskAdminInstall />;
      default:
        return null;
    }
  }

  const ActiveIcon = TABS.find((t) => t.id === activeTab)?.icon || LayoutDashboard;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-50 flex flex-col md:flex-row"
    >
      {/* ----------------------------------------------------------------- */}
      {/* Desktop sidebar                                                   */}
      {/* ----------------------------------------------------------------- */}
      <aside className="hidden md:flex flex-col w-64 bg-white border-r border-slate-200 flex-none">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-800">Kiosk Admin</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {deviceInfo?.location_name || 'Device'}
          </p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1" role="navigation" aria-label="Admin navigation">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-slate-900 text-white'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="w-5 h-5 flex-none" />
                {tab.label}
              </button>
            );
          })}
        </nav>

        {/* Exit button */}
        <div className="p-3 border-t border-slate-100">
          <Button
            variant="ghost"
            className="w-full justify-start gap-3 text-slate-500 hover:text-slate-800"
            onClick={onClose}
          >
            <LogOut className="w-5 h-5" />
            Exit Admin
          </Button>
        </div>
      </aside>

      {/* ----------------------------------------------------------------- */}
      {/* Mobile top bar                                                    */}
      {/* ----------------------------------------------------------------- */}
      <header className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-slate-200 flex-none">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-600 transition-colors"
            aria-label="Toggle navigation menu"
            aria-expanded={mobileMenuOpen}
          >
            <ActiveIcon className="w-5 h-5" />
          </button>
          <h2 className="text-base font-semibold text-slate-800">
            {TABS.find((t) => t.id === activeTab)?.label || 'Admin'}
          </h2>
        </div>
        <button
          onClick={onClose}
          className="p-2 -mr-2 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close admin panel"
        >
          <X className="w-5 h-5" />
        </button>
      </header>

      {/* Mobile dropdown menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-b border-slate-200 overflow-hidden flex-none"
          >
            <nav className="px-3 py-2 space-y-0.5" role="navigation" aria-label="Admin navigation">
              {TABS.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-slate-100 text-slate-900'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <span className="flex items-center gap-3">
                      <Icon className="w-5 h-5 flex-none" />
                      {tab.label}
                    </span>
                    {isActive && <ChevronRight className="w-4 h-4 text-slate-400" />}
                  </button>
                );
              })}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ----------------------------------------------------------------- */}
      {/* Main content area                                                 */}
      {/* ----------------------------------------------------------------- */}
      <main className="flex-1 overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            variants={CONTENT_VARIANTS}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="h-full"
          >
            {renderContent()}
          </motion.div>
        </AnimatePresence>
      </main>
    </motion.div>
  );
}
